import React from "react";
import styled, { css } from "styled-components";
import { useApolloClient } from "@apollo/react-hooks";
import CancelIcon from "@material-ui/icons/Cancel";
import momentTz from "moment-timezone";
import { PortalFunctionParams } from "react-portal";
import { RouteComponentProps, withRouter } from "react-router";
import {
  Dropdown,
  DropdownSource,
} from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import {
  getEndAt,
  getScheduledDuration,
  getStartAt,
} from "@sonder/ui/source/styled/components/ScheduleLayer/helpers";
import {
  RenderTriggerProps,
  TimePicker,
} from "@sonder/ui/source/styled/components/TimePicker";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { PanelCardSectionHeader } from "@sonder/ui/source/styled/fragments/PanelCardSectionHeader";
import {
  RelatedTasksBlockProps,
  RelatedTasksStatus,
} from "@sonder/ui/source/styled/fragments/ScheduleLayer";
import {
  SchedulerColors,
  SchedulerShadows,
  SchedulerTextColors,
} from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { ONE_MINUTE } from "core/constants";
import { useCurrentUserTimezone } from "core/hooks/useCurrentTimeZone";
import { RelatedTaskScheduleFragment } from "core/queries/GetTaskSchedules";
import { TaskData } from "core/queries/GetTasks";
import {
  getCleaningRequestUrl,
  getTaskAssigneeIds,
  getTaskCompanyIds,
} from "core/queries/mappers";
import { humanizeTime } from "core/utils/humanizeTime";
import { snakeToCamelCase } from "core/utils/snakeToCamelCase";
import { titlelizeCase } from "core/utils/titlelizeCase";
import { setUnscheduledTask } from "schedule/actions";
import { useFeatureFlags } from "schedule/hooks/useFeatureFlags";
import { ViewedItem } from "schedule/reducer";
import { useStore } from "schedule/store";
import {
  getSpaceCleanInternalRef,
  getStatus,
} from "schedule/utils/scheduleUtils";
import {
  AssigneePicker,
  AssigneePickerTrigger,
} from "tasks/components/AssigneePicker";
import { AssigneePickerLoader } from "tasks/components/AssigneePicker.loader";
import { formatAccountName, parseGraphQLId } from "tasks/helpers";
import { TaskStatus } from "typings/globalGQLTypes";

const StyledQuickView = styled.div`
  min-width: 22rem;
`;

const sharedAssigneePickerStyles = css`
  border-radius: 0;
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-start;
  max-width: inherit;
  width: 100%;
`;

const StyledAssigneePickerLoader = styled(AssigneePickerLoader)`
  ${sharedAssigneePickerStyles}
`;

const StyledAssigneePickerTrigger = styled(AssigneePickerTrigger)`
  ${sharedAssigneePickerStyles}

  &:hover:not(:disabled) {
    background: none;
    border: 1px solid transparent;
  }
`;

const StyledAssigneePickerWrapper = styled.div`
  display: block;
  margin-top: 0.5rem;
`;

const StyledQuickViewHeader = styled.div<RelatedTasksBlockProps>`
  align-items: center;
  background: ${props => SchedulerColors[props.status]};
  background-size: 56.57px 56.57px;
  box-shadow: inset 0 -0.25rem 0 ${props => SchedulerShadows[props.status]};
  border-radius: 0.3rem;
  color: ${props => SchedulerTextColors[props.status]};
  display: flex;
  font-weight: bold;
  height: 3.14rem;
  padding: 0 1rem;
`;

const StyledQuickViewContent = styled.div`
  padding: 0.25rem 1rem;
`;

const StyledGridRow = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
`;

const StyledGridColumn = styled.div`
  margin-bottom: 0.5rem;
`;

const StyledGridContent = styled.div`
  align-items: center;
  display: flex;
  min-height: 1.7rem;
`;

const StyledScheduleQuickView = styled.div`
  margin: 0.5rem;

  div + div {
    margin-top: 0.5rem;
  }
`;

const StyledMoreDetails = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0.25rem 1rem;
`;

const StyledScheduleQuickViewHeader = styled.span`
  font-size: 1.25rem;
  margin-right: 1rem;
`;

interface ScheduleQuickViewProps
  extends Omit<ViewedItem, "boundingRect">,
    PortalFunctionParams {}

interface TaskQuickViewProps
  extends Omit<ViewedItem, "boundingRect">,
    RouteComponentProps,
    PortalFunctionParams {}

const InlineTimePickerTrigger = ({ date, disabled }: RenderTriggerProps) => (
  <Button disabled={disabled}>
    {date ? date.format("h:mm A") : "Set Time"}
  </Button>
);

export const ScheduleQuickViewContents = ({
  booking,
}: ScheduleQuickViewProps) => {
  const { track } = useSegment();
  const { state: authState } = useAuthStore();
  const {
    state: { dateRange },
  } = useStore();
  const userTimezone = useCurrentUserTimezone();
  if (!booking) return null;

  const name =
    (booking?.account && formatAccountName(booking?.account)) || "Unknown";
  const endOfDayStartTime = momentTz
    .tz(booking.checkInTime, userTimezone)
    .endOf("day");
  const endOfDayEndtime = momentTz
    .tz(booking.checkOutTime, userTimezone)
    .endOf("day");
  const endOfDayDateRangeStartAt = momentTz
    .tz(dateRange.startAt, userTimezone)
    .endOf("day");
  const totalDays = endOfDayEndtime.diff(endOfDayStartTime, "day");
  const isSingleDay = totalDays <= 1;
  const currentDays = Math.min(
    endOfDayDateRangeStartAt.diff(endOfDayStartTime, "day"),
    totalDays,
  );
  const formattedStartAt = momentTz
    .tz(booking.checkInTime, userTimezone)
    .format(isSingleDay ? "h:mm A" : "MMM D");
  const formattedEndAt = momentTz
    .tz(booking.checkOutTime, userTimezone)
    .format(isSingleDay ? "h:mm A" : "MMM D, YYYY");
  return (
    <StyledScheduleQuickView>
      <div>
        <StyledScheduleQuickViewHeader>{name}</StyledScheduleQuickViewHeader>
        {!isSingleDay && (
          <span>
            {currentDays}/{totalDays} days
          </span>
        )}
      </div>
      <div>
        {formattedStartAt} - {formattedEndAt}
      </div>
      {booking.bookingUrl && authState?.user?.isLegacyAdmin && (
        <div>
          <Link
            href={booking.bookingUrl}
            target="_blank"
            onClick={() =>
              track({
                action: "click",
                element: "booking_more_details_link",
                eventName: "quick_view",
                props: { bookingId: booking.id },
              })
            }
          >
            More details
          </Link>
        </div>
      )}
    </StyledScheduleQuickView>
  );
};

export const TaskQuickViewContents = withRouter(
  ({ history, closePortal, place, task: cachedTask }: TaskQuickViewProps) => {
    const { track } = useSegment();
    const { canViewCleaningDetails } = useFeatureFlags();
    const { state: authState } = useAuthStore();
    const userTimezone = useCurrentUserTimezone();
    const { dispatch } = useStore();
    const client = useApolloClient();
    const taskId = cachedTask?.id || "";
    const task = client.readFragment({
      id: `Task:${taskId}`,
      fragment: RelatedTaskScheduleFragment,
    });
    const {
      dueAt,
      completedAt,
      estimatedDuration,
      scheduledStartAt,
      startedAt,
    } = task;
    const eventName = "quick_view";
    const status = getStatus(task) as RelatedTasksStatus;
    const headerTexts = {
      archived: "Archived",
      canceled: "Canceled",
      complete: "Completed",
      inprogress: "In Progress",
      late: "Late",
      ready: "Not Started",
      unassigned: "Not Assigned",
      waiting: "Waiting",
    };
    const headerText = headerTexts[status];
    const startAt = getStartAt({
      startedAt,
      dueAt,
      estimatedDuration,
      scheduledStartAt,
    });
    const endAt = getEndAt({
      startedAt,
      estimatedDuration,
      completedAt,
      dueAt,
    });
    const cleaningRequestUrl = getCleaningRequestUrl(task, authState?.user);
    const companyIds = getTaskCompanyIds(task as unknown as TaskData);
    const scheduledDuration = getScheduledDuration(startAt, endAt);
    const cleaningDetailsUrl = `/schedule/${parseGraphQLId(taskId)}`;
    const moreDetailsLink =
      (canViewCleaningDetails && (
        <Link
          href={cleaningDetailsUrl}
          onClick={e => {
            track({
              action: "click",
              element: "task_cleaning_details_link",
              eventName: "quick_view",
              props: { taskId },
            });
            history.push(cleaningDetailsUrl);
            closePortal();
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          More details
        </Link>
      )) ||
      (cleaningRequestUrl && (
        <Link
          href={cleaningRequestUrl}
          target="_blank"
          onClick={() =>
            track({
              action: "click",
              element: "task_legacy_more_details_link",
              eventName: "quick_view",
              props: { taskId },
            })
          }
        >
          More details
        </Link>
      )) ||
      null;

    return (
      <StyledQuickView>
        <StyledQuickViewHeader status={status}>
          {headerText}
        </StyledQuickViewHeader>
        <StyledAssigneePickerWrapper>
          <AssigneePicker
            assigneeIds={getTaskAssigneeIds(task as unknown as TaskData)}
            companyIds={companyIds}
            contentLoader={<StyledAssigneePickerLoader />}
            eventName={eventName}
            refetchQueries={["GetSchedulerMetrics"]}
            renderTrigger={({ hasMultipleSupport, assignees, onClear }) => {
              return hasMultipleSupport ? (
                <StyledAssigneePickerTrigger assignees={assignees} />
              ) : (
                <StyledAssigneePickerTrigger
                  assignees={assignees}
                  onClear={onClear}
                />
              );
            }}
            tags={task.tags}
            taskCityId={task.city?.id?.toString()}
            taskId={task.id}
            taskType={task.type}
          />
        </StyledAssigneePickerWrapper>
        <StyledQuickViewContent>
          <StyledGridRow>
            {place && !place.cufSpaceType && (
              <StyledGridColumn>
                <PanelCardSectionHeader>Unit info</PanelCardSectionHeader>
                <StyledGridContent>
                  {`${place.brCount || 0} bed(s) |
                ${place.nbrBathrooms || 0} bath(s)`}
                </StyledGridContent>
              </StyledGridColumn>
            )}
            {place && place.cufSpaceType && (
              <StyledGridColumn>
                <PanelCardSectionHeader>Internal Ref</PanelCardSectionHeader>
                <StyledGridContent>
                  {titlelizeCase(getSpaceCleanInternalRef(place))}
                </StyledGridContent>
              </StyledGridColumn>
            )}
            {task.cleaningRequest?.category && (
              <StyledGridColumn>
                <PanelCardSectionHeader>Clean type</PanelCardSectionHeader>
                <StyledGridContent>
                  {titlelizeCase(
                    snakeToCamelCase(task.cleaningRequest?.category),
                  )}
                </StyledGridContent>
              </StyledGridColumn>
            )}
          </StyledGridRow>
          <StyledGridRow>
            <StyledGridColumn>
              <PanelCardSectionHeader>
                Estimated duration
              </PanelCardSectionHeader>
              <StyledGridContent>
                {estimatedDuration
                  ? humanizeTime(estimatedDuration * ONE_MINUTE)
                  : "Not Set"}
              </StyledGridContent>
            </StyledGridColumn>
            <StyledGridColumn>
              <PanelCardSectionHeader>
                Scheduled duration
              </PanelCardSectionHeader>
              <StyledGridContent>
                {scheduledDuration
                  ? humanizeTime(scheduledDuration * ONE_MINUTE)
                  : "Not Set"}
              </StyledGridContent>
            </StyledGridColumn>
          </StyledGridRow>
          {(startAt || endAt) && (
            <StyledGridRow>
              {startAt && (
                <StyledGridColumn>
                  <PanelCardSectionHeader>Start At</PanelCardSectionHeader>
                  <TimePicker
                    disabled
                    date={momentTz.tz(startAt, userTimezone)}
                    renderTrigger={InlineTimePickerTrigger}
                  />
                </StyledGridColumn>
              )}
              {endAt && (
                <StyledGridColumn>
                  <PanelCardSectionHeader>Due by</PanelCardSectionHeader>
                  <TimePicker
                    disabled
                    date={momentTz.tz(endAt, userTimezone)}
                    renderTrigger={InlineTimePickerTrigger}
                  />
                </StyledGridColumn>
              )}
            </StyledGridRow>
          )}
        </StyledQuickViewContent>
        <StyledMoreDetails>
          <Button
            iconLeft
            noBorder
            disabled={[
              TaskStatus.canceled,
              TaskStatus.complete,
              TaskStatus.deleted,
            ].includes(task.status)}
            onClick={() => {
              dispatch(setUnscheduledTask(task.id));
              closePortal();
            }}
          >
            <CancelIcon />
            Unschedule
          </Button>
          {moreDetailsLink}
        </StyledMoreDetails>
      </StyledQuickView>
    );
  },
);

interface QuickViewProps extends ViewedItem {
  isOpen: boolean;
  onClose?: () => void;
}

export const QuickView = ({
  boundingRect,
  isOpen,
  onClose,
  place,
  booking,
  task,
}: QuickViewProps) => {
  const isScheduleViewContent = booking && !task;

  return (
    <Dropdown
      boundingRect={boundingRect}
      isOpen={isOpen}
      source={DropdownSource.QuickView}
      onClose={onClose}
    >
      {renderProps => (
        <DropdownContent enableMinWidth={false}>
          {isScheduleViewContent && (
            <ScheduleQuickViewContents booking={booking} {...renderProps} />
          )}
          {!isScheduleViewContent && (
            <TaskQuickViewContents place={place} task={task} {...renderProps} />
          )}
        </DropdownContent>
      )}
    </Dropdown>
  );
};
